import { useCallback, useEffect, useRef, type RefObject } from 'react';
import {
  GRID_ROOT_GROUP_ID,
  gridRowGroupingSanitizedModelSelector,
  gridRowTreeSelector,
  type GridApiPremium,
  type GridDataSourceGroupNode,
  type GridGroupNode,
} from '@mui/x-data-grid-premium';
import { gridStateStorageKey } from './gridState';

/**
 * Remember which row groups the user opened, per grid on this device — the same transient
 * "where I left off" layer as the stored layout (useGridStatePersistence), kept under its own
 * key: MUI's exportState carries the grouping model but not which groups are open, and the
 * named saved views stay about columns, sort and filter.
 *
 * A server-side group (a grid with a `dataSource`) is identified by its path of (grouping field, key)
 * pairs, so the same key under two grouping models is two different groups; ungrouping or regrouping
 * removes the entries of the grouping that was left. A tree-data group (`treeData`) is identified by
 * its row id, which the grid's rows keep across reloads. Only groups in the other state than the
 * default are stored — open ones while collapsed is the default — so returning a group to the
 * default removes its entry. A tree grid may default to expanded (`treeExpandedByDefault`); a
 * server-side group is collapsed by default. Any other group keeps the grid's default.
 *
 * Returns the `isGroupExpandedByDefault` the grid takes. MUI asks it for every group it builds —
 * on mount, after a refetch and after a regrouping — and with a server-side data source it
 * fetches the children of every group it answers true for.
 */
export function useGroupExpansionPersistence(
  apiRef: RefObject<GridApiPremium | null>,
  gridKey: string,
  { treeExpandedByDefault = false }: { treeExpandedByDefault?: boolean } = {},
): (node: GridGroupNode) => boolean {
  const storageKey = gridStateStorageKey(`${gridKey}:expandedGroups`);
  const expandedRef = useRef<{ key: string; paths: Set<string> } | null>(null);

  const expandedPaths = useCallback((): Set<string> => {
    if (expandedRef.current?.key !== storageKey) {
      expandedRef.current = { key: storageKey, paths: readExpandedPaths(storageKey) };
    }
    return expandedRef.current.paths;
  }, [storageKey]);

  // The state a group has when nothing is stored for it, and its stored identity.
  const defaultOf = useCallback(
    (node: GridGroupNode) => (isDataSourceGroup(node) ? false : treeExpandedByDefault),
    [treeExpandedByDefault],
  );
  // No identity for a group the grid made up itself (client-side row grouping): it keeps the grid's default.
  const identityOf = (node: GridGroupNode, model: string[]): string | null => {
    if (isDataSourceGroup(node)) return groupPath(node, model);
    return node.isAutoGenerated ? null : treeGroupId(node);
  };

  const isGroupExpandedByDefault = useCallback(
    (node: GridGroupNode) => {
      const identity = identityOf(node, groupingModel(apiRef));
      return identity === null ? false : defaultOf(node) !== expandedPaths().has(identity);
    },
    [apiRef, expandedPaths, defaultOf], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    const api = apiRef.current;
    if (!api?.subscribeEvent) return undefined;
    return api.subscribeEvent('rowExpansionChange', (node: GridGroupNode) => {
      const paths = expandedPaths();
      const model = groupingModel(apiRef);
      // Expand all / collapse all report the root; every group in the tree changed.
      const changed = node.id === GRID_ROOT_GROUP_ID ? groupNodes(apiRef) : [node];
      for (const group of changed) {
        const identity = identityOf(group, model);
        if (identity === null) continue;
        if (Boolean(group.childrenExpanded) !== defaultOf(group)) paths.add(identity);
        else paths.delete(identity);
      }
      writeExpandedPaths(storageKey, paths);
    });
  }, [apiRef, expandedPaths, storageKey, defaultOf]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ungrouping, or grouping on other fields, leaves the groups stored under the old grouping
  // pointing at nothing: forget them. A level dropped from the end keeps the levels above it.
  useEffect(() => {
    const api = apiRef.current;
    if (!api?.subscribeEvent) return undefined;
    return api.subscribeEvent('rowGroupingModelChange', (model: string[]) => {
      const paths = expandedPaths();
      for (const path of [...paths]) {
        if (!isTreeGroupId(path) && !isUnderGrouping(path, model)) paths.delete(path);
      }
      writeExpandedPaths(storageKey, paths);
    });
  }, [apiRef, expandedPaths, storageKey]);

  return isGroupExpandedByDefault;
}

function groupingModel(apiRef: RefObject<GridApiPremium | null>): string[] {
  return apiRef.current ? gridRowGroupingSanitizedModelSelector(apiRef as RefObject<GridApiPremium>) : [];
}

function isDataSourceGroup(node: GridGroupNode): node is GridDataSourceGroupNode {
  return Array.isArray((node as Partial<GridDataSourceGroupNode>).path);
}

const TREE_PREFIX = 'tree:';

/** A tree-data group's identity: its row id. */
function treeGroupId(node: GridGroupNode): string {
  return `${TREE_PREFIX}${String(node.id)}`;
}

function isTreeGroupId(identity: string): boolean {
  return identity.startsWith(TREE_PREFIX);
}

/** The group's identity across reloads: each level's grouping field with its key. */
function groupPath(node: GridDataSourceGroupNode, model: string[]): string {
  return JSON.stringify(node.path.map((key, depth) => [model[depth] ?? null, key]));
}

/** Whether a stored group still exists under this grouping: every level's field is still there. */
function isUnderGrouping(path: string, model: string[]): boolean {
  try {
    const levels: unknown = JSON.parse(path);
    return Array.isArray(levels)
      && levels.length <= model.length
      && levels.every((level, depth) => Array.isArray(level) && level[0] === model[depth]);
  } catch {
    return false;
  }
}

function groupNodes(apiRef: RefObject<GridApiPremium | null>): GridGroupNode[] {
  if (!apiRef.current) return [];
  const tree = gridRowTreeSelector(apiRef as RefObject<GridApiPremium>);
  return Object.values(tree).filter(
    (n): n is GridGroupNode => n.type === 'group' && n.id !== GRID_ROOT_GROUP_ID,
  );
}

function readExpandedPaths(storageKey: string): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeExpandedPaths(storageKey: string, paths: Set<string>): void {
  try {
    if (paths.size === 0) localStorage.removeItem(storageKey);
    else localStorage.setItem(storageKey, JSON.stringify([...paths]));
  } catch {
    // ignore quota / storage-blocked errors — this is a convenience, not data
  }
}
